import React, { ReactNode, useCallback } from 'react';
import {
  EQUIPMENT_LIST_CREATE,
  EQUIPMENT_LIST_VIEW,
} from '../../../router/routes';
import { ActiveFilter } from '../../../components/common/table/active-filter';
import { TFunction, useTranslation } from 'react-i18next';
import { route } from '../../../services/utils';
import { useService } from '../../../services/services';
import useDefaultTableTopbar from '../../../components/common/table/default-topbar';
import Table, { TableColumn } from '../../../components/common/table/table';
import css from './index.module.css';
import {
  EquipmentBaseFields,
  MachineAndAggregateFields,
  SelfPropelledMachineryFields,
} from '../../../interfaces/equipment';
import { EquipmentType } from '../../../enums/equipment-type';
import ShowIfAllowed from '../../../components/common/show-if-allowed';

function getEquipmentBaseColumns(
  t: TFunction
): TableColumn<EquipmentBaseFields>[] {
  return [
    {
      accessor: 'type_value' /* Veids */,
      header: t('pages.equipment.type'),
      sortable: true,
      className: css['strong'],
    },
    {
      accessor: 'make' /* Marka */,
      header: t('pages.equipment.make'),
      sortable: true,
      className: css['strong'],
    },
    {
      accessor: 'model' /* Modelis */,
      header: t('pages.equipment.model'),
      sortable: true,
    },
  ];
}

function getSelfPropelledMachineryColumns(
  t: TFunction
): TableColumn<SelfPropelledMachineryFields | EquipmentBaseFields>[] {
  return Object.assign([], getEquipmentBaseColumns(t)).concat([
    {
      accessor: 'registration_number' /* Reģistrācijas Nr. */,
      header: t('pages.equipment.registration_number'),
    },
    {
      accessor: 'registration_year' /* Reģistrācijas gads */,
      header: t('pages.equipment.registration_year'),
    },
    {
      accessor: 'horsepower' /* Jauda, Zs */,
      header: t('pages.equipment.horsepower'),
    },
    {
      accessor: 'active',
      header: t('active'),
      filter: <ActiveFilter />,
    },
  ]);
}

function getMachineAndAggregateColumns(
  t: TFunction
): TableColumn<MachineAndAggregateFields | EquipmentBaseFields>[] {
  return Object.assign([], getEquipmentBaseColumns(t)).concat([
    {
      accessor: 'width' /* Platums */,
      header: t('pages.equipment.width'),
    },
    {
      accessor: 'measurement' /* Mērvienība */,
      header: t('pages.equipment.measurement'),
    },
    {
      accessor: 'active',
      header: t('active'),
      filter: <ActiveFilter />,
    },
  ]);
}

function getTableColumns(
  t: TFunction,
  equipmentType: EquipmentType
): TableColumn<SelfPropelledMachineryFields | MachineAndAggregateFields>[] {
  switch (equipmentType) {
    case EquipmentType.selfPropelledMachinery:
      return getSelfPropelledMachineryColumns(t);
    case EquipmentType.machinesAndAggregates:
      return getMachineAndAggregateColumns(t);
    default:
      return getEquipmentBaseColumns(t);
  }
}

type EquipmentListProps = {
  farmId: string;
  equipmentType: EquipmentType;
  children?: ReactNode;
};

export default function EquipmentListTab({
  farmId,
  equipmentType,
  children,
}: EquipmentListProps) {
  const { t } = useTranslation();

  const [TopbarComponent] = useDefaultTableTopbar(
    route(EQUIPMENT_LIST_CREATE, { farmId, equipmentType })
  );

  // the request that will allow us to get the table data
  const { equipmentService } = useService();
  const request = useCallback(
    (page: number, sort: string, desc: boolean, filter: any) =>
      equipmentService.index(farmId, {
        page,
        sort,
        desc,
        equipment_type: equipmentType,
        ...filter,
      }),
    [equipmentService, equipmentType, farmId]
  );

  // get the columns for our current equipment type
  const tableColumns = (): TableColumn<
    SelfPropelledMachineryFields | MachineAndAggregateFields
  >[] => getTableColumns(t, equipmentType);

  return (
    <div className={css['body']}>
      <ShowIfAllowed ability={{ title: 'create equipment' }}>
        {TopbarComponent}
      </ShowIfAllowed>
      <Table<SelfPropelledMachineryFields | MachineAndAggregateFields>
        request={request}
        keyAccessor="id"
        columns={tableColumns()}
        viewRoute={route(EQUIPMENT_LIST_VIEW, { farmId, equipmentType })}
        viewRecordAbility={{ title: 'view equipment' }}
      />
    </div>
  );
}
